import { lazyWithReload } from "./utils/lazyWithReload";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UploadQueueProvider } from "./context/UploadQueueContext";
import Layout from "./components/Layout";
import RequirePermission from "./components/RequirePermission";
import TermsGate from "./components/TermsGate";
import Login from "./pages/Login";

// Every page below Layout is code-split. Before this, all 17 pages shipped in
// one bundle: a technician opening /dashboard downloaded and parsed the whole
// app, including ~600kB of FullCalendar for a page they never open and the
// Tools pages their role can't even reach.
//
// Layout and Login stay EAGER on purpose. Layout is on the critical path for
// every authenticated view, and Login is the first paint for a logged-out
// visitor — deferring either would trade a faster bundle for a slower first
// screen, which is the wrong direction.
//
// The Suspense boundary these need lives in Layout, around <Outlet>, so the
// header and nav stay mounted during a chunk fetch. See components/Layout.jsx.
const DashboardPage = lazyWithReload(() => import("./pages/DashboardPage"));
const CalendarPage = lazyWithReload(() => import("./pages/CalendarPage"));
const Projects = lazyWithReload(() => import("./pages/Projects"));
const ProjectPage = lazyWithReload(() => import("./pages/ProjectPage"));
const Jobs = lazyWithReload(() => import("./pages/Jobs"));
const JobPage = lazyWithReload(() => import("./pages/JobPage"));
const Visits = lazyWithReload(() => import("./pages/Visits"));
const PurchaseOrders = lazyWithReload(() => import("./pages/PurchaseOrders"));
const CustomerPage = lazyWithReload(() => import("./pages/CustomerPage"));
const PropertyPage = lazyWithReload(() => import("./pages/PropertyPage"));
const ProfilePage = lazyWithReload(() => import("./pages/ProfilePage"));
const Audit = lazyWithReload(() => import("./pages/Audit"));
const Tools = lazyWithReload(() => import("./pages/Tools"));
const TimeCardPage = lazyWithReload(() => import("./pages/TimeCardPage"));
const HrCenterPage = lazyWithReload(() => import("./pages/HrCenterPage"));
const Estimates = lazyWithReload(() => import("./pages/Estimates"));
const EnclosureQuotes = lazyWithReload(() => import("./pages/EnclosureQuotes"));
const EnclosureQuoteDetail = lazyWithReload(() => import("./pages/EnclosureQuoteDetail"));
const SettingsPage = lazyWithReload(() => import("./pages/SettingsPage"));
const SearchResults = lazyWithReload(() => import("./pages/SearchResults"));
const KnowledgeBase = lazyWithReload(() => import("./pages/KnowledgeBase"));
const OrgChart = lazyWithReload(() => import("./pages/OrgChart"));
const Contacts = lazyWithReload(() => import("./pages/Contacts"));
const VendorPage = lazyWithReload(() => import("./pages/VendorPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

function PrivateRoute({ children }) {
  const { isAuthed, loading, me } = useAuth();
  if (loading) return null;
  if (!isAuthed) return <Navigate to="/" replace />;
  // Blocks every private route — including job/visit detail pages a
  // technician reaches without a section permission — behind the STINTE
  // STACK confidentiality/acceptable-use notice until they've clicked
  // "I Agree" at least once. `me` can still be null for an instant right
  // after sign-in (the /me round trip hasn't landed yet); that renders
  // nothing rather than flashing the gate before there's anything to check.
  if (!me) return null;
  // ⚠️ Skipped while "View As" is active (`me.view_as` truthy — see
  // AuthContext.startViewAs). `me.terms_accepted_at` here is the TARGET's,
  // and clicking "I Agree" on their behalf would both be a real, lasting
  // write to their account and — since it's a POST — refused outright by the
  // backend's read-only gate, stranding the admin on a gate they can never
  // clear. Viewing what a screen looks like isn't "using the app" as that
  // person, so the consent gate doesn't apply here.
  if (!me.terms_accepted_at && !me.view_as) return <TermsGate />;
  return children;
}

function PublicRoute({ children }) {
  const { isAuthed, loading } = useAuth();
  if (loading) return null;
  return !isAuthed ? children : <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
      <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/scheduler" element={<Navigate to="/calendar" replace />} />
        <Route path="/tasks" element={<Navigate to="/dashboard" replace />} />
        <Route path="/projects" element={<RequirePermission perm="section.projects.view"><Projects /></RequirePermission>} />
        <Route path="/projects/:projectId" element={<RequirePermission perm="section.projects.view"><ProjectPage /></RequirePermission>} />
        <Route path="/map" element={<Navigate to="/dashboard?tab=map" replace />} />
        <Route path="/jobs" element={<RequirePermission perm="section.jobs.view"><Jobs /></RequirePermission>} />
        {/* ⚠️⚠️ DETAIL ROUTES ARE DELIBERATELY NOT GUARDED. A technician reaches
            a job from their Dashboard and holds no `section.jobs.view` — guarding
            /jobs/:id would break their day-to-day work. This is the frontend
            mirror of the nav-vs-API split in auth/sections.py: the LIST pages are
            the "section", the detail pages are the work. The server is still the
            real boundary either way; these guards are UX. */}
        <Route path="/jobs/:id" element={<JobPage />} />
        <Route path="/visits" element={<RequirePermission perm="section.visits.view"><Visits /></RequirePermission>} />
        {/* Travel is a purchase order (po_type "travel"), not its own section —
            the separate page was folded into /purchase-orders on Sep 6, 2026.
            The redirect keeps old links and bookmarks working. */}
        <Route path="/travel" element={<Navigate to="/purchase-orders" replace />} />
        <Route path="/purchase-orders" element={<RequirePermission perm="section.purchase_orders.view"><PurchaseOrders /></RequirePermission>} />
        {/* The registry moved into Settings (owner ask, Sep 9 2026), the same
            way /users did. The two detail routes below stay standalone. */}
        <Route path="/customers" element={<Navigate to="/settings?section=customers" replace />} />
        <Route path="/customers/:customerId" element={<CustomerPage />} />
        <Route path="/properties/:propertyId" element={<PropertyPage />} />
        {/* Users moved into Settings — keep old links/bookmarks working. */}
        <Route path="/users" element={<Navigate to="/settings?section=users" replace />} />
        <Route path="/profile/:userId" element={<ProfilePage />} />
        <Route path="/audit" element={<RequirePermission perm="section.audit.view"><Audit /></RequirePermission>} />
        <Route path="/tools" element={<RequirePermission perm="section.pmo_tools.view"><Tools /></RequirePermission>} />
        <Route path="/org-chart" element={<OrgChart />} />
        <Route path="/vendors/:vendorId" element={<RequirePermission perm="section.settings_vendors.view"><VendorPage /></RequirePermission>} />
        <Route path="/contacts" element={<RequirePermission perm="section.contacts.view"><Contacts /></RequirePermission>} />
        <Route path="/knowledge-base" element={<RequirePermission perm="section.knowledge_base.view"><KnowledgeBase /></RequirePermission>} />
        <Route path="/hr" element={<RequirePermission perm="section.hr_center.view"><HrCenterPage /></RequirePermission>} />
        <Route path="/time-card" element={<RequirePermission perm="section.time_cards.view"><TimeCardPage /></RequirePermission>} />
        <Route path="/estimates" element={<RequirePermission perm="section.pricing.view"><Estimates /></RequirePermission>} />
        <Route path="/enclosure-quotes" element={<RequirePermission perm="section.enclosure_quotes.view"><EnclosureQuotes /></RequirePermission>} />
        <Route path="/enclosure-quotes/:id" element={<EnclosureQuoteDetail />} />
        <Route path="/settings" element={<RequirePermission perm="section.settings.view"><SettingsPage /></RequirePermission>} />
        <Route path="/search" element={<SearchResults />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <UploadQueueProvider>
            <AppRoutes />
          </UploadQueueProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
